import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeftRight, ArrowRight, Check, ChevronDown, GitBranch, Loader2, Route, Search } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Label } from '../components_dashboard/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components_dashboard/ui/alert-dialog';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const BASE = `${apiUrl}/api/v1/admin/move-prompt`;

const PROMPT_TYPES = [
  {
    key: 'single',
    label: 'Single Prompt',
    icon: Route,
    hint: 'Move one static agent prompt version.',
  },
  {
    key: 'multi',
    label: 'Workflow',
    icon: GitBranch,
    hint: 'Clone a workflow with fresh internal IDs.',
  },
];

const DIRECTIONS = [
  { key: 'staging-staging', from: 'staging', to: 'staging', label: 'Staging -> Staging', hint: 'Clone within staging' },
  { key: 'staging-main', from: 'staging', to: 'main', label: 'Staging -> Main', hint: 'Promote staged data to production' },
  { key: 'main-staging', from: 'main', to: 'staging', label: 'Main -> Staging', hint: 'Pull production data into staging' },
  { key: 'main-main', from: 'main', to: 'main', label: 'Main -> Main', hint: 'Clone within production' },
];

function envBadge(env) {
  const cls = env === 'main'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : 'bg-amber-50 text-amber-700 ring-amber-200';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ring-1 ${cls}`}>
      {env}
    </span>
  );
}

function Select({ label, value, onChange, options, placeholder, disabled, loading, hint, required }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label || '').toLowerCase().includes(q));
  }, [options, query]);

  const selected = options.find((o) => o.value === value);
  const isDisabled = disabled || loading;

  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1 text-sm font-medium text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
        {loading && <Loader2 size={12} className="ml-1 animate-spin text-slate-400" />}
      </Label>
      <div ref={wrapRef} className="relative">
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => setOpen((o) => !o)}
          className="flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
        >
          <span className={`truncate ${selected ? 'text-slate-900' : 'text-muted-foreground'}`}>
            {selected ? (selected.content || selected.label) : (placeholder || 'Select...')}
          </span>
          <ChevronDown size={16} className={`shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && !isDisabled && (
          <div className="absolute z-50 mt-1 flex max-h-72 w-full flex-col overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md">
            <div className="relative border-b">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter..."
                className="w-full bg-transparent py-2 pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-1">
              {options.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">No options</div>
              ) : filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">No matches.</div>
              ) : (
                filtered.map((opt) => {
                  const active = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { onChange(opt.value); setOpen(false); }}
                      className={`relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground ${active ? 'font-medium' : ''} ${opt.className || ''}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-left">{opt.content || opt.label}</span>
                      {active && (
                        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                          <Check size={14} />
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

export default function MovePromptDashboard() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [promptType, setPromptType] = useState('single');
  const [direction, setDirection] = useState(DIRECTIONS[1]);

  const [srcUsers, setSrcUsers] = useState([]);
  const [srcUserId, setSrcUserId] = useState('');
  const [srcAgents, setSrcAgents] = useState([]);
  const [srcAgentId, setSrcAgentId] = useState('');
  const [srcVersions, setSrcVersions] = useState([]);
  const [srcVersionId, setSrcVersionId] = useState('');
  const [srcWorkflows, setSrcWorkflows] = useState([]);
  const [srcWorkflowId, setSrcWorkflowId] = useState('');
  const [loadingSrcUsers, setLoadingSrcUsers] = useState(false);
  const [loadingSrcAgents, setLoadingSrcAgents] = useState(false);
  const [loadingSrcVersions, setLoadingSrcVersions] = useState(false);
  const [loadingSrcWorkflows, setLoadingSrcWorkflows] = useState(false);

  const [tgtUsers, setTgtUsers] = useState([]);
  const [tgtUserId, setTgtUserId] = useState('');
  const [tgtAgents, setTgtAgents] = useState([]);
  const [tgtAgentId, setTgtAgentId] = useState('');
  const [tgtVersions, setTgtVersions] = useState([]);
  const [tgtWorkflows, setTgtWorkflows] = useState([]);
  const [loadingTgtUsers, setLoadingTgtUsers] = useState(false);
  const [loadingTgtAgents, setLoadingTgtAgents] = useState(false);
  const [loadingTgtVersions, setLoadingTgtVersions] = useState(false);
  const [loadingTgtWorkflows, setLoadingTgtWorkflows] = useState(false);

  const [newName, setNewName] = useState('');
  const [newAgentName, setNewAgentName] = useState('');
  const [createNewAgent, setCreateNewAgent] = useState(false);
  const [overwriteVersion, setOverwriteVersion] = useState(true);
  const [targetVersionId, setTargetVersionId] = useState('');
  const [targetWorkflowId, setTargetWorkflowId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchUsers = useCallback(async (env, setter, setLoading) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/users?env=${env}`, auth);
      setter(data.users || []);
    } catch (err) {
      toast.error(`Failed to load ${env} users`);
      setter([]);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const fetchAgents = useCallback(async (env, userId, setter, setLoading) => {
    if (!userId) {
      setter([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE}/agents?env=${env}&user_id=${encodeURIComponent(userId)}`,
        auth,
      );
      setter(data.agents || []);
    } catch (err) {
      toast.error(`Failed to load ${env} agents`);
      setter([]);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const fetchVersions = useCallback(async (env, agentId) => {
    if (!agentId) {
      setSrcVersions([]);
      return;
    }
    setLoadingSrcVersions(true);
    try {
      const { data } = await axios.get(
        `${BASE}/versions?env=${env}&agent_id=${encodeURIComponent(agentId)}`,
        auth,
      );
      setSrcVersions(data.versions || []);
    } catch (err) {
      toast.error(`Failed to load ${env} versions`);
      setSrcVersions([]);
    } finally {
      setLoadingSrcVersions(false);
    }
  }, [auth]);

  const fetchTargetVersions = useCallback(async (env, agentId) => {
    if (!agentId) {
      setTgtVersions([]);
      return;
    }
    setLoadingTgtVersions(true);
    try {
      const { data } = await axios.get(
        `${BASE}/versions?env=${env}&agent_id=${encodeURIComponent(agentId)}`,
        auth,
      );
      setTgtVersions(data.versions || []);
    } catch (err) {
      toast.error(`Failed to load ${env} target versions`);
      setTgtVersions([]);
    } finally {
      setLoadingTgtVersions(false);
    }
  }, [auth]);

  const fetchWorkflows = useCallback(async (env, userId, setter = setSrcWorkflows, setLoading = setLoadingSrcWorkflows) => {
    if (!userId) {
      setter([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BASE}/workflows?env=${env}&user_id=${encodeURIComponent(userId)}`,
        auth,
      );
      setter(data.workflows || []);
    } catch (err) {
      toast.error(`Failed to load ${env} workflows`);
      setter([]);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  const resetSelections = useCallback(() => {
    setSrcUserId('');
    setSrcAgentId('');
    setSrcVersionId('');
    setSrcWorkflowId('');
    setSrcAgents([]);
    setSrcVersions([]);
    setSrcWorkflows([]);
    setTgtUserId('');
    setTgtAgentId('');
    setTgtAgents([]);
    setTgtVersions([]);
    setTgtWorkflows([]);
    setOverwriteVersion(true);
    setCreateNewAgent(false);
    setNewAgentName('');
    setTargetVersionId('');
    setTargetWorkflowId('');
    setNewName('');
    setResult(null);
  }, []);

  useEffect(() => {
    resetSelections();
    fetchUsers(direction.from, setSrcUsers, setLoadingSrcUsers);
    fetchUsers(direction.to, setTgtUsers, setLoadingTgtUsers);
  }, [direction, promptType, fetchUsers, resetSelections]);

  useEffect(() => {
    setSrcAgentId('');
    setSrcVersionId('');
    setSrcWorkflowId('');
    setSrcAgents([]);
    setSrcVersions([]);
    setSrcWorkflows([]);
    if (promptType === 'single') {
      fetchAgents(direction.from, srcUserId, setSrcAgents, setLoadingSrcAgents);
    } else {
      fetchWorkflows(direction.from, srcUserId, setSrcWorkflows, setLoadingSrcWorkflows);
    }
  }, [direction, promptType, srcUserId, fetchAgents, fetchWorkflows]);

  useEffect(() => {
    setSrcVersionId('');
    if (promptType === 'single') {
      fetchVersions(direction.from, srcAgentId);
    }
  }, [direction, promptType, srcAgentId, fetchVersions]);

  useEffect(() => {
    setTgtAgentId('');
    setTgtAgents([]);
    setTgtVersions([]);
    setTgtWorkflows([]);
    setTargetVersionId('');
    setTargetWorkflowId('');
    setOverwriteVersion(true);
    if (promptType === 'single') {
      fetchAgents(direction.to, tgtUserId, setTgtAgents, setLoadingTgtAgents);
    } else {
      fetchWorkflows(direction.to, tgtUserId, setTgtWorkflows, setLoadingTgtWorkflows);
    }
  }, [direction, promptType, tgtUserId, fetchAgents, fetchWorkflows]);

  useEffect(() => {
    setTargetVersionId('');
    setTgtVersions([]);
    if (promptType === 'single') {
      fetchTargetVersions(direction.to, tgtAgentId);
    }
  }, [direction, promptType, tgtAgentId, fetchTargetVersions]);

  const activeType = PROMPT_TYPES.find((item) => item.key === promptType) || PROMPT_TYPES[0];
  const selectedSourceUser = srcUsers.find((u) => u.id === srcUserId);
  const selectedTargetUser = tgtUsers.find((u) => u.id === tgtUserId);
  const selectedSourceAgent = srcAgents.find((a) => a.agent_id === srcAgentId);
  const selectedTargetAgent = tgtAgents.find((a) => a.agent_id === tgtAgentId);
  const selectedVersion = srcVersions.find((v) => v.version_id === srcVersionId);
  const selectedTargetVersion = tgtVersions.find((v) => v.version_id === targetVersionId);
  const selectedWorkflow = srcWorkflows.find((w) => w.id === srcWorkflowId);
  const selectedTargetWorkflow = tgtWorkflows.find((w) => w.id === targetWorkflowId);

  const canSubmit = promptType === 'single'
    ? !!srcAgentId
      && !!srcVersionId
      && !!tgtUserId
      && (createNewAgent
        ? newAgentName.trim().length > 0
        : !!tgtAgentId && (overwriteVersion ? !!targetVersionId : newName.trim().length > 0))
      && !submitting
    : !!srcUserId
      && !!srcWorkflowId
      && !!tgtUserId
      && (overwriteVersion ? !!targetWorkflowId : newName.trim().length > 0)
      && !submitting;

  const openConfirm = () => {
    if (!canSubmit) return;
    setResult(null);
    setConfirmOpen(true);
  };

  const handleConfirmMove = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const body = promptType === 'single'
        ? (createNewAgent
          ? {
              prompt_type: 'single',
              from_env: direction.from,
              to_env: direction.to,
              source_agent_id: srcAgentId,
              source_version_id: srcVersionId,
              target_user_id: tgtUserId,
              create_new_agent: true,
              new_agent_name: newAgentName.trim(),
              new_version_name: newName.trim() || undefined,
            }
          : {
              prompt_type: 'single',
              from_env: direction.from,
              to_env: direction.to,
              source_agent_id: srcAgentId,
              source_version_id: srcVersionId,
              target_user_id: tgtUserId,
              target_agent_id: tgtAgentId,
              target_version_id: overwriteVersion ? targetVersionId : undefined,
              overwrite_existing_version: overwriteVersion,
              new_version_name: overwriteVersion ? undefined : newName.trim(),
            })
        : {
            prompt_type: 'multi',
            from_env: direction.from,
            to_env: direction.to,
            source_user_id: srcUserId,
            source_workflow_id: srcWorkflowId,
            target_user_id: tgtUserId,
            target_workflow_id: overwriteVersion ? targetWorkflowId : undefined,
            overwrite_existing_workflow: overwriteVersion,
            new_agent_name: overwriteVersion ? undefined : newName.trim(),
            new_version_name: overwriteVersion ? undefined : newName.trim(),
          };
      const { data } = await axios.post(BASE, body, auth);
      setResult(data);
      if (data.prompt_type === 'multi') {
        toast.success(
          data.overwrite_existing_workflow
            ? `Workflow overwritten ${data.from_env} -> ${data.to_env}. Workflow kept: ${data.new_workflow_id}`
            : `Workflow moved ${data.from_env} -> ${data.to_env}. New agent: ${data.new_agent_id}`,
        );
      } else if (data.create_new_agent || data.new_agent_id) {
        toast.success(
          `New agent created ${data.from_env} -> ${data.to_env}. Agent: ${data.new_agent_id}`,
        );
      } else {
        toast.success(
          data.overwrite_existing_version
            ? `Prompt overwritten ${data.from_env} -> ${data.to_env}. Version kept: ${data.new_version_id}`
            : `Prompt moved ${data.from_env} -> ${data.to_env}. New version: ${data.new_version_id}`,
        );
      }
      resetSelections();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Move failed';
      toast.error(typeof detail === 'string' ? detail : 'Move failed');
      setResult({ status: 'error', detail });
    } finally {
      setSubmitting(false);
    }
  };

  const userOptions = (list) =>
    list
      .filter((u) => !!u.id)
      .map((u) => ({
        value: u.id,
        label: u.name ? `${u.name}${u.email ? ` (${u.email})` : ''}` : (u.email || u.id),
      }));

  const agentOptions = (list) =>
    list
      .filter((a) => !!a.agent_id)
      .map((a) => ({
        value: a.agent_id,
        label: a.agent_name ? `${a.agent_name} - ${a.agent_id}` : a.agent_id,
      }));

  const versionOptions = (list) =>
    list
      .filter((v) => !!v.version_id)
      .map((v) => ({
        value: v.version_id,
        label: `${v.version_name || '(no name)'} - ${v.version_id}${v.status ? ` [${v.status}]` : ''}`,
      }));

  const workflowOptions = (list) =>
    list
      .filter((w) => !!w.id)
      .map((w) => {
        const name = w.agent_name || w.version_name || 'Untitled workflow';
        return {
          value: w.id,
          label: name,
          textValue: name,
          className: 'py-1.5 text-sm',
          content: (
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-slate-900">{name}</span>
              <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-700">
                Workflow
              </span>
            </span>
          ),
        };
      });

  return (
    <div className="flex min-h-screen bg-slate-50">
      
      <main className="ml-[56px] flex-1 px-6 py-8 md:ml-[240px]">
        <div className="max-w-5xl">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-primary">
            <ArrowLeftRight size={14} /> Admin Controls
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Move Prompt</h1>
          <p className="mt-1 text-sm text-slate-500">
            Copy single-agent prompt versions or clone multi-agent workflows between staging and main.
          </p>

          <div className="mt-5 flex w-fit flex-wrap rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
            {PROMPT_TYPES.map((type) => {
              const Icon = type.icon;
              const active = type.key === promptType;
              return (
                <button
                  key={type.key}
                  type="button"
                  onClick={() => setPromptType(type.key)}
                  className={`flex items-center gap-2 rounded-[14px] px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    active
                      ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  <Icon size={15} />
                  {type.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-slate-500">{activeType.hint}</p>

          <div className="mt-5 flex w-fit flex-wrap rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
            {DIRECTIONS.map((d) => {
              const active = d.key === direction.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => setDirection(d)}
                  className={`rounded-[14px] px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    active
                      ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 flex items-center gap-2 text-xs text-slate-500">
            {envBadge(direction.from)}
            <ArrowRight size={12} className="text-slate-400" />
            {envBadge(direction.to)}
            <span className="ml-1">{direction.hint}</span>
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Source</h2>
                {envBadge(direction.from)}
              </div>
              <div className="space-y-4">
                <Select
                  label="User"
                  required
                  value={srcUserId}
                  onChange={setSrcUserId}
                  options={userOptions(srcUsers)}
                  placeholder="Select source user..."
                  loading={loadingSrcUsers}
                />
                {promptType === 'single' ? (
                  <>
                    <Select
                      label="Agent"
                      required
                      value={srcAgentId}
                      onChange={setSrcAgentId}
                      options={agentOptions(srcAgents)}
                      placeholder={srcUserId ? 'Select source agent...' : 'Select a user first'}
                      disabled={!srcUserId}
                      loading={loadingSrcAgents}
                    />
                    <Select
                      label="Version"
                      required
                      value={srcVersionId}
                      onChange={setSrcVersionId}
                      options={versionOptions(srcVersions)}
                      placeholder={srcAgentId ? 'Select version to move...' : 'Select an agent first'}
                      disabled={!srcAgentId}
                      loading={loadingSrcVersions}
                    />
                  </>
                ) : (
                  <Select
                    label="Workflow"
                    required
                    value={srcWorkflowId}
                    onChange={setSrcWorkflowId}
                    options={workflowOptions(srcWorkflows)}
                    placeholder={srcUserId ? 'Select workflow to move...' : 'Select a user first'}
                    disabled={!srcUserId}
                    loading={loadingSrcWorkflows}
                  />
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Target</h2>
                {envBadge(direction.to)}
              </div>
              <div className="space-y-4">
                <Select
                  label="User"
                  required
                  value={tgtUserId}
                  onChange={setTgtUserId}
                  options={userOptions(tgtUsers)}
                  placeholder="Select target user..."
                  loading={loadingTgtUsers}
                />
                {promptType === 'single' && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Agent Mode</Label>
                      <div className="flex w-fit flex-wrap rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
                        {[
                          { key: 'existing', label: 'Select Agent' },
                          { key: 'new', label: 'New Agent' },
                        ].map((mode) => {
                          const active = createNewAgent
                            ? mode.key === 'new'
                            : mode.key === 'existing';
                          return (
                            <button
                              key={mode.key}
                              type="button"
                              onClick={() => {
                                const toNew = mode.key === 'new';
                                setCreateNewAgent(toNew);
                                setOverwriteVersion(true);
                                setTargetVersionId('');
                                if (toNew) {
                                  setTgtAgentId('');
                                  setTgtVersions([]);
                                } else {
                                  setNewAgentName('');
                                }
                              }}
                              disabled={!tgtUserId}
                              className={`rounded-[14px] px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                                active
                                  ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                                  : 'text-slate-400 hover:text-slate-600'
                              } ${!tgtUserId ? 'cursor-not-allowed opacity-50' : ''}`}
                            >
                              {mode.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500">
                        {createNewAgent
                          ? 'Create a fresh target agent with a new agent id.'
                          : 'Use an existing target agent.'}
                      </p>
                    </div>
                    {createNewAgent ? (
                      <div>
                        <Label htmlFor="new_agent_name" className="text-sm font-medium text-slate-700">
                          New Agent Name
                          <span className="text-rose-500"> *</span>
                        </Label>
                        <Input
                          id="new_agent_name"
                          value={newAgentName}
                          onChange={(e) => setNewAgentName(e.target.value)}
                          placeholder="e.g. Production support agent"
                          disabled={!tgtUserId}
                          className="mt-1 text-sm"
                        />
                        <p className="mt-1 text-xs text-slate-500">
                          A new agent id will be created under the target user with the moved version as Version 1.
                        </p>
                      </div>
                    ) : (
                      <>
                        <Select
                          label="Agent"
                          required
                          value={tgtAgentId}
                          onChange={setTgtAgentId}
                          options={agentOptions(tgtAgents)}
                          placeholder={tgtUserId ? 'Select target agent...' : 'Select a user first'}
                          disabled={!tgtUserId}
                          loading={loadingTgtAgents}
                        />
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-700">Move Mode</Label>
                          <div className="flex w-fit flex-wrap rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
                            {[
                              { key: 'overwrite', label: 'Overwrite Version' },
                              { key: 'new', label: 'New Version' },
                            ].map((mode) => {
                              const active = overwriteVersion
                                ? mode.key === 'overwrite'
                                : mode.key === 'new';
                              return (
                                <button
                                  key={mode.key}
                                  type="button"
                                  onClick={() => setOverwriteVersion(mode.key === 'overwrite')}
                                  disabled={!tgtAgentId}
                                  className={`rounded-[14px] px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                                    active
                                      ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                                      : 'text-slate-400 hover:text-slate-600'
                                  } ${!tgtAgentId ? 'cursor-not-allowed opacity-50' : ''}`}
                                >
                                  {mode.label}
                                </button>
                              );
                            })}
                          </div>
                          <p className="text-xs text-slate-500">
                            {overwriteVersion
                              ? 'Replace the content of a target version and keep the same version id.'
                              : 'Create a fresh target version with a new version id.'}
                          </p>
                        </div>
                        {overwriteVersion && (
                          <Select
                        label="Target Version"
                        required
                        value={targetVersionId}
                        onChange={setTargetVersionId}
                        options={versionOptions(tgtVersions)}
                        placeholder={tgtAgentId ? 'Select target version to overwrite...' : 'Select a target agent first'}
                        disabled={!tgtAgentId}
                        loading={loadingTgtVersions}
                      />
                    )}
                      </>
                    )}
                  </>
                )}
                {promptType === 'multi' && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Move Mode</Label>
                      <div className="flex w-fit flex-wrap rounded-2xl border border-slate-200/60 bg-slate-100/80 p-1.5 shadow-inner">
                        {[
                          { key: 'new', label: 'New Workflow' },
                          { key: 'overwrite', label: 'Overwrite Workflow' },
                        ].map((mode) => {
                          const active = overwriteVersion
                            ? mode.key === 'overwrite'
                            : mode.key === 'new';
                          return (
                            <button
                              key={mode.key}
                              type="button"
                              onClick={() => setOverwriteVersion(mode.key === 'overwrite')}
                              disabled={!tgtUserId}
                              className={`rounded-[14px] px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                                active
                                  ? 'border border-slate-100 bg-white text-violet-700 shadow-md'
                                  : 'text-slate-400 hover:text-slate-600'
                              } ${!tgtUserId ? 'cursor-not-allowed opacity-50' : ''}`}
                            >
                              {mode.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-500">
                        {overwriteVersion
                          ? 'Replace the content of a target workflow and keep the same workflow id.'
                          : 'Create a fresh target workflow with new workflow, agent, version, and field ids.'}
                      </p>
                    </div>
                    {overwriteVersion && (
                      <Select
                        label="Target Workflow"
                        required
                        value={targetWorkflowId}
                        onChange={setTargetWorkflowId}
                        options={workflowOptions(tgtWorkflows)}
                        placeholder={tgtUserId ? 'Select target workflow to overwrite...' : 'Select a target user first'}
                        disabled={!tgtUserId}
                        loading={loadingTgtWorkflows}
                      />
                    )}
                  </>
                )}
                {(!overwriteVersion || (promptType === 'single' && createNewAgent)) && (
                  <div>
                    <Label htmlFor="new_prompt_name" className="text-sm font-medium text-slate-700">
                      {promptType === 'single' ? 'New Version Name' : 'New Workflow Name'}
                      {promptType === 'single' && createNewAgent ? <span className="text-slate-400"> (optional)</span> : <span className="text-rose-500"> *</span>}
                    </Label>
                    <Input
                      id="new_prompt_name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={
                        promptType === 'single'
                          ? 'e.g. Promoted from staging 2026-06-02'
                          : 'e.g. Production workflow 2026-06-02'
                      }
                      disabled={promptType === 'single' ? (createNewAgent ? !tgtUserId : (!tgtAgentId || overwriteVersion)) : !tgtUserId}
                      className="mt-1 text-sm"
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      {promptType === 'single'
                        ? (createNewAgent
                          ? 'Optional — defaults to the source version name. It becomes Version 1 on the new agent.'
                          : overwriteVersion
                            ? 'The selected target version is replaced in place and keeps the same version id.'
                            : 'The selected version is appended to the target agent.')
                        : 'The workflow is cloned into the target user with new agent, version, node, input, and output IDs.'}
                    </p>
                  </div>
                )}
                {promptType === 'multi' && overwriteVersion && (
                  <p className="text-xs text-slate-500">
                    The selected target workflow is replaced in place and keeps the same workflow id.
                  </p>
                )}
                <div className="rounded-md border border-slate-100 bg-slate-50 p-3 text-xs text-slate-600">
                  {promptType === 'single'
                    ? (createNewAgent
                      ? 'A new agent with a new agent id will be created. Existing target agents are untouched.'
                      : overwriteVersion
                        ? 'Only the selected target version is overwritten. Other target versions stay untouched.'
                        : 'Existing target versions are untouched.')
                    : overwriteVersion
                      ? 'Only the selected target workflow is overwritten. Other target workflows stay untouched.'
                      : 'Existing target workflows are untouched.'}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button onClick={openConfirm} disabled={!canSubmit} className="min-w-[150px]">
              {submitting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Moving...
                </>
              ) : (
                <>
                  Move Prompt
                  <ArrowRight size={14} className="ml-2" />
                </>
              )}
            </Button>
          </div>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Confirm prompt move</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-2">
                      {envBadge(direction.from)}
                      <ArrowRight size={12} className="text-slate-400" />
                      {envBadge(direction.to)}
                      <span className="text-slate-500">{activeType.label}</span>
                    </div>

                    <dl className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed">
                      <div className="grid grid-cols-3 gap-2">
                        <dt className="text-slate-500">Source user</dt>
                        <dd className="col-span-2 break-all font-medium text-slate-800">
                          {selectedSourceUser?.name || selectedSourceUser?.email || srcUserId || '-'}
                        </dd>
                      </div>
                      {promptType === 'single' ? (
                        <>
                          <div className="mt-1 grid grid-cols-3 gap-2">
                            <dt className="text-slate-500">Source agent</dt>
                            <dd className="col-span-2 break-all font-medium text-slate-800">
                              {selectedSourceAgent?.agent_name || '-'}
                              <span className="text-slate-400"> ({srcAgentId})</span>
                            </dd>
                          </div>
                          <div className="mt-1 grid grid-cols-3 gap-2">
                            <dt className="text-slate-500">Source version</dt>
                            <dd className="col-span-2 break-all font-medium text-slate-800">
                              {selectedVersion?.version_name || '(no name)'}
                              <span className="text-slate-400"> ({srcVersionId})</span>
                            </dd>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="mt-1 grid grid-cols-3 gap-2">
                            <dt className="text-slate-500">Workflow</dt>
                            <dd className="col-span-2 break-all font-medium text-slate-800">
                              {selectedWorkflow?.agent_name || selectedWorkflow?.version_name || '-'}
                              <span className="text-slate-400"> ({srcWorkflowId})</span>
                            </dd>
                          </div>
                          {overwriteVersion && (
                            <div className="mt-1 grid grid-cols-3 gap-2">
                              <dt className="text-slate-500">Target workflow</dt>
                              <dd className="col-span-2 break-all font-medium text-slate-800">
                                {selectedTargetWorkflow?.agent_name || selectedTargetWorkflow?.version_name || '-'}
                                <span className="text-slate-400"> ({targetWorkflowId})</span>
                              </dd>
                            </div>
                          )}
                        </>
                      )}
                      <div className="my-2 border-t border-slate-200" />
                      <div className="grid grid-cols-3 gap-2">
                        <dt className="text-slate-500">Target user</dt>
                        <dd className="col-span-2 break-all font-medium text-slate-800">
                          {selectedTargetUser?.name || selectedTargetUser?.email || tgtUserId || '-'}
                        </dd>
                      </div>
                      {promptType === 'single' && (
                        <>
                          <div className="mt-1 grid grid-cols-3 gap-2">
                            <dt className="text-slate-500">Target agent</dt>
                            <dd className="col-span-2 break-all font-medium text-slate-800">
                              {createNewAgent ? (
                                <>New agent: {newAgentName.trim() || '-'}</>
                              ) : (
                                <>
                                  {selectedTargetAgent?.agent_name || '-'}
                                  <span className="text-slate-400"> ({tgtAgentId})</span>
                                </>
                              )}
                            </dd>
                          </div>
                          {overwriteVersion && !createNewAgent && (
                            <div className="mt-1 grid grid-cols-3 gap-2">
                              <dt className="text-slate-500">Target version</dt>
                              <dd className="col-span-2 break-all font-medium text-slate-800">
                                {selectedTargetVersion?.version_name || '(no name)'}
                                <span className="text-slate-400"> ({targetVersionId})</span>
                              </dd>
                            </div>
                          )}
                        </>
                      )}
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        <dt className="text-slate-500">
                          {promptType === 'single'
                            ? (createNewAgent ? 'New agent' : overwriteVersion ? 'Overwrite mode' : 'New version')
                            : (overwriteVersion ? 'Overwrite mode' : 'New workflow')}
                        </dt>
                        <dd className="col-span-2 break-all font-medium text-violet-700">
                          {createNewAgent
                            ? `${newAgentName.trim()}${newName.trim() ? ` / ${newName.trim()}` : ''}`
                            : overwriteVersion
                              ? promptType === 'single'
                                ? 'Overwrite selected target version in place'
                                : 'Overwrite selected target workflow in place'
                              : newName.trim()}
                        </dd>
                      </div>
                    </dl>

                    <p className="text-xs text-slate-500">
                      {promptType === 'single'
                        ? (createNewAgent
                          ? 'A fresh agent_id and version_id will be created under the target user.'
                          : overwriteVersion
                            ? 'The target version content will be replaced and the version_id will stay the same.'
                            : 'A fresh version_id and timestamp will be created.')
                        : overwriteVersion
                          ? 'The target workflow content will be replaced and the workflow id will stay the same.'
                          : 'A fresh workflow, agent_id, version_id, node IDs, call_infields_id, and call_outfields_id will be created.'}
                    </p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>No, cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmMove}>
                  Yes, move prompt
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {result && (
            <div
              className={`mt-4 rounded-lg border p-4 text-sm ${
                result.status === 'success'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-rose-200 bg-rose-50 text-rose-800'
              }`}
            >
              <pre className="whitespace-pre-wrap font-mono text-xs">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
